import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import CTABand from "../components/CTABand";
import {
  HeartHandshake, Hand, Handshake, Megaphone, ArrowRight, DollarSign, Users, Sparkles,
} from "lucide-react";
import { WAYS_TO_HELP } from "../data/pages";
import { PHOTOS } from "../data/photos";

const ICONS = { HeartHandshake, Hand, Handshake, Megaphone };

const WaysToHelpPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <PageHero
          eyebrow="Ways to Help"
          title="Be part of the recovery movement."
          description="There are many ways to multiply impact across Central Florida — from giving and volunteering to partnering and advocating. Pick the path that fits where you are."
          breadcrumbs={[{ label: "Ways to Help" }]}
          image={PHOTOS.conversation}
        />

        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="grid md:grid-cols-2 gap-5">
              {WAYS_TO_HELP.map((w, i) => {
                const Icon = ICONS[w.icon] || HeartHandshake;
                return (
                  <article
                    key={w.id}
                    id={w.id}
                    className="rc-card-hover relative bg-[hsl(var(--rc-paper))] border border-[hsl(var(--border))] rounded-2xl p-8 md:p-10 overflow-hidden"
                  >
                    <div className="flex items-start gap-5">
                      <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--rc-deep))] text-white flex items-center justify-center shrink-0">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
                          0{i + 1}
                        </div>
                        <h3 className="font-serif-display mt-1 text-[26px] md:text-[30px] font-semibold text-[hsl(var(--rc-deep))]">
                          {w.title}
                        </h3>
                        <p className="mt-3 text-[15px] leading-[1.7] text-[hsl(var(--rc-slate))]">
                          {w.body}
                        </p>
                        <Link
                          to={w.cta.href}
                          className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-[hsl(var(--rc-deep))] hover:text-[hsl(var(--rc-purple))] transition-colors group"
                        >
                          {w.cta.label}
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Where your gift goes */}
        <section className="py-20 md:py-24 rc-section-soft">
          <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
                Where your gift goes
              </p>
              <h2 className="font-serif-display mt-3 text-[34px] md:text-[42px] leading-[1.1] font-semibold text-[hsl(var(--rc-deep))]">
                Every dollar multiplies in lived experience.
              </h2>
              <p className="mt-5 text-[16px] leading-[1.75] text-[hsl(var(--rc-slate))]">
                As a peer-led RCO, our overhead stays low — so your gift moves quickly into the hands and hearts of the specialists doing the work.
              </p>
              <Link
                to="/donate"
                className="mt-7 inline-flex items-center gap-2 px-6 h-12 rounded-full bg-[hsl(var(--rc-deep))] hover:bg-[hsl(var(--rc-purple))] text-white text-[14px] font-medium transition-colors"
              >
                Donate Now <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {[
                { amt: "$25", icon: DollarSign, body: "Provides recovery toolkit materials for one new participant." },
                { amt: "$100", icon: Sparkles, body: "Funds an hour of peer-led group programming at a community center." },
                { amt: "$250", icon: Users, body: "Powers a full day of mobile outreach in an underserved neighborhood." },
                { amt: "$1,000", icon: HeartHandshake, body: "Supports a peer specialist with training, supervision and tools for a month." },
              ].map(({ amt, icon: Icon, body }) => (
                <div
                  key={amt}
                  className="bg-white border border-[hsl(var(--border))] rounded-2xl p-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[hsl(var(--rc-purple-soft))] flex items-center justify-center text-[hsl(var(--rc-purple))]">
                      <Icon className="w-4.5 h-4.5" />
                    </div>
                    <div className="font-serif-display text-[24px] font-semibold text-[hsl(var(--rc-deep))]">
                      {amt}
                    </div>
                  </div>
                  <p className="mt-4 text-[14px] leading-[1.65] text-[hsl(var(--rc-slate))]">
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABand
          eyebrow="Ready when you are"
          title="Let’s start a conversation."
          body="Tell us how you’d like to plug in. Our team will follow up with the right person to make it real."
          primary={{ label: "Contact Us", href: "/contact" }}
          secondary={{ label: "Donate Now", href: "/donate" }}
        />
      </main>
      <Footer />
    </div>
  );
};

export default WaysToHelpPage;
